// Feature #374 — ask for a technical skill the catalogue lacks, from inside
// an assessment.
//
// A modal rather than a page on purpose: the ticket's first requirement is
// that lodging the request must not take the assessor away from the form
// they are filling in. The form's state is untouched while this is open,
// and the shared Modal's dirty guard stops a stray click on the backdrop
// throwing away a half-written justification.
//
// The request is a proposal, not a skill. It goes to the Skill Requests
// queue for an sm_admin to accept or reject; the requester is notified
// either way, and an accepted skill appears in the form's picker.
import React, { useEffect, useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { createSkillRequest, SmSkillRequest } from '@/api/sm'

const inputStyle: React.CSSProperties = {
  padding: '8px 10px',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'var(--font-family)',
  width: '100%',
}

const labelStyle: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--color-dark-grey)',
  display: 'block',
  marginBottom: '6px',
}

const primaryButton: React.CSSProperties = {
  padding: '9px 20px',
  backgroundColor: 'var(--color-magenta)',
  color: 'var(--color-white)',
  border: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--font-family)',
}

const secondaryButton: React.CSSProperties = {
  padding: '9px 20px',
  backgroundColor: 'transparent',
  color: 'var(--color-dark-grey)',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '6px',
  fontSize: '14px',
  cursor: 'pointer',
  fontFamily: 'var(--font-family)',
}

interface Props {
  isOpen: boolean
  /** Pre-fills the name — the text the assessor had typed into the picker
   *  when nothing matched, so they do not type it twice. */
  initialName?: string
  /** Whose assessment this is lodged from. Lets the accept notification link
   *  back here and the form show the outcome against the right person. */
  subjectUserId: string
  onClose: () => void
  onCreated: (request: SmSkillRequest) => void
}

export const RequestSkillDialog: React.FC<Props> = ({
  isOpen,
  initialName = '',
  subjectUserId,
  onClose,
  onCreated,
}) => {
  const [name, setName] = useState(initialName)
  const [justification, setJustification] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Reset for each opening so a second request never starts with the first
  // one's justification still in the box.
  useEffect(() => {
    if (!isOpen) return
    setName(initialName)
    setJustification('')
    setError(null)
    setSubmitting(false)
  }, [isOpen, initialName])

  const dirty = name.trim() !== initialName.trim() || justification.trim().length > 0

  const handleSubmit = async () => {
    setError(null)
    if (!name.trim()) {
      setError('Give the skill a name.')
      return
    }
    if (!justification.trim()) {
      setError('Say why the catalogue needs it — the reviewer decides from this.')
      return
    }
    setSubmitting(true)
    try {
      const created = await createSkillRequest({
        name: name.trim(),
        justification: justification.trim(),
        subject_user_id: subjectUserId,
      })
      onCreated(created)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Could not lodge the request')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Request a new skill"
      size="md"
      isDirty={dirty}
      dirtyConfirmMessage="Discard this skill request? Your assessment is unaffected either way."
      footer={
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={secondaryButton} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            style={{ ...primaryButton, cursor: submitting ? 'wait' : 'pointer' }}
            disabled={submitting}
          >
            {submitting ? 'Sending…' : 'Send request'}
          </button>
        </div>
      }
    >
      <div style={{ display: 'grid', gap: '16px' }}>
        <p style={{ fontSize: '13px', color: 'var(--color-dark-grey)', margin: 0 }}>
          Can&rsquo;t find a skill in the catalogue? Ask for it here. A Skills
          Administrator reviews every request; you&rsquo;ll be notified when it is
          accepted or declined, and an accepted skill appears in the picker on this
          form. Your assessment stays exactly as it is.
        </p>
        {error && (
          <div
            role="alert"
            style={{
              padding: '10px 12px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              color: '#b91c1c',
              fontSize: '13px',
            }}
          >
            {error}
          </div>
        )}
        <div>
          <label htmlFor="skill-request-name" style={labelStyle}>
            Skill name
          </label>
          <input
            id="skill-request-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={255}
            placeholder="e.g. Terraform"
            style={inputStyle}
          />
          <p style={{ fontSize: '12px', color: 'var(--color-dark-grey)', margin: '6px 0 0' }}>
            Specific enough to resource against — &ldquo;Terraform&rdquo; rather than
            &ldquo;infrastructure&rdquo;.
          </p>
        </div>
        <div>
          <label htmlFor="skill-request-justification" style={labelStyle}>
            Why should it be in the catalogue?
          </label>
          <textarea
            id="skill-request-justification"
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            rows={5}
            maxLength={2000}
            placeholder="Where it comes up, who needs it, why nothing in the catalogue already covers it…"
            style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
          />
        </div>
      </div>
    </Modal>
  )
}

export default RequestSkillDialog
